import React, { useState } from "react"
import { Image, Pressable, View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import AnnotationCanvas from "../../../../common/liveMode/liveV3/AnnotationCanvas"
import MaterialExplanationDisplay from "../../../../common/components/material/MaterialExplanationDisplay"
import FeedbackButton from "../../../../common/components/feedback/FeedbackButton"

const FloatingControls = ({ visualState, showVisualImage, setShowVisualImage, trackingPrefix = "" }) => (
    <View style={{ position: "absolute", top: 8, right: 8, zIndex: 20, alignItems: "flex-end", gap: 6 }}>
        {/* Before generation: generate button */}
        {!visualState.visualExplanationUrl && !visualState.isPolling && !visualState.hasTimedOut && (
            <FilledButtonM
                trackingId={`whiteboard-generate-visual${trackingPrefix}`}
                onPress={() => visualState.handleGenerateClick?.()}
                label="Générer visuel"
                icon="image"
                color={theme.colors.primary}
                style={{ height: 32, marginBottom: 0, width: null }}
                contentStyle={{ height: 32, paddingHorizontal: 8 }}
                labelStyle={{ fontSize: 11 }}
            />
        )}
        {/* During generation: spinner */}
        {visualState.isPolling && (
            <View style={{ flexDirection: "row", alignItems: "center", backgroundColor: "rgba(255,255,255,0.9)", borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 }}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <TextM fontSize={11} color={theme.colors.grey} style={{ marginLeft: 6 }}>Génération...</TextM>
            </View>
        )}
        {/* After generation: eye toggle */}
        {visualState.visualExplanationUrl && (
            <Pressable
                onPress={() => setShowVisualImage((v) => !v)}
                style={{ flexDirection: "row", alignItems: "center", backgroundColor: "rgba(255,255,255,0.9)", borderRadius: 8, paddingVertical: 6, paddingHorizontal: 10 }}
            >
                <FontAwesome5
                    name={showVisualImage ? "eye" : "eye-slash"}
                    size={14}
                    color={theme.colors.primary}
                    solid
                />
                <TextM fontSize={12} fontWeight="Medium" color={theme.colors.primary} style={{ marginLeft: 6 }}>Visuel</TextM>
            </Pressable>
        )}
        {/* Timeout: visual not available */}
        {visualState.hasTimedOut && !visualState.visualExplanationUrl && (
            <View style={{ backgroundColor: "rgba(255,255,255,0.9)", borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 }}>
                <TextM fontSize={11} color={theme.colors.grey}>Visuel non disponible</TextM>
            </View>
        )}
    </View>
)

const FeedbackRow = ({ visualState }) => (
    <View style={{ flexDirection: "row", justifyContent: "center", paddingVertical: 6 }}>
        <FeedbackButton
            onPress={() => visualState.handleFeedback?.("positive")}
            isSelected={visualState.currentFeedback?.feedback_type === "positive"}
            isPositive={true}
            tooltipText="Visuel utile"
            minimal={true}
            size={{ icon: 16, text: 8 }}
        />
        <View style={{ width: 8 }} />
        <FeedbackButton
            onPress={() => visualState.handleFeedback?.("negative")}
            isSelected={visualState.currentFeedback?.feedback_type === "negative"}
            isPositive={false}
            tooltipText="Visuel peu utile"
            minimal={true}
            size={{ icon: 16, text: 8 }}
        />
    </View>
)

const WhiteboardTab = React.memo((props) => {
    const {
        material,
        user,
        isFocusShareActive,
        isDrawing,
        contentLayout,
        whiteboardCanvasRef,
        getStrokesRef,
        lastActiveCanvasRef,
        setStrokeCount,
    } = props

    const [showVisualImage, setShowVisualImage] = useState(true)
    const [visualState, setVisualState] = useState({})

    return (
        <View style={{ flex: 1, backgroundColor: "#FFFFFF" }}>
            {isFocusShareActive ? (
                <>
                    {/* Image + canvas zone */}
                    <View style={{ flex: 1, position: "relative" }}>
                        {/* Image underlay - shown when visual is generated and toggle is on */}
                        {showVisualImage && visualState.visualExplanationUrl && (
                            <Image
                                source={{ uri: visualState.visualExplanationUrl }}
                                style={{ flex: 1, width: "100%" }}
                                resizeMode="contain"
                            />
                        )}
                        {/* Drawing canvas */}
                        <AnnotationCanvas
                            ref={whiteboardCanvasRef}
                            width={contentLayout.width}
                            height={contentLayout.height}
                            color="#5CE0D8"
                            strokeWidth={4}
                            enabled={isDrawing}
                            strokesRef={getStrokesRef("whiteboard")}
                            onStrokeEnd={() => {
                                lastActiveCanvasRef.current = whiteboardCanvasRef.current
                                setStrokeCount((s) => s + 1)
                            }}
                        />
                        <FloatingControls
                            visualState={visualState}
                            showVisualImage={showVisualImage}
                            setShowVisualImage={setShowVisualImage}
                        />
                    </View>
                    {/* Feedback row - below the image, visible when image is shown */}
                    {showVisualImage && visualState.visualExplanationUrl && (
                        <FeedbackRow visualState={visualState} />
                    )}
                </>
            ) : (
                <View style={{ flex: 1, position: "relative" }}>
                    <FloatingControls
                        visualState={visualState}
                        showVisualImage={showVisualImage}
                        setShowVisualImage={setShowVisualImage}
                        trackingPrefix="-preshare"
                    />
                    {/* Image underlay when visual is shown */}
                    {showVisualImage && visualState.visualExplanationUrl && (
                        <>
                            <Image
                                source={{ uri: visualState.visualExplanationUrl }}
                                style={{ flex: 1, width: "100%" }}
                                resizeMode="contain"
                            />
                            <FeedbackRow visualState={visualState} />
                        </>
                    )}
                </View>
            )}
            {/* Headless MaterialExplanationDisplay — drives generation/polling */}
            <MaterialExplanationDisplay
                material={material}
                user={user}
                headless
                onStateChange={setVisualState}
            />
        </View>
    )
})

export default WhiteboardTab
